import { useState, useCallback, useEffect } from 'react';
import { checkUserConnectionRequest } from '../services/firebase/startup-opportunities';
import { useAuthContext } from './useAuthContext';

/**
 * Custom hook for managing connection status between users and opportunities
 * Provides connection status checking, state management, and refresh event handling
 */
export const useConnectionStatus = (opportunityId, refreshTrigger = null) => {
  const { user } = useAuthContext();
  const [connectionStatus, setConnectionStatus] = useState({
    hasRequest: false,
    status: null,
    loading: true
  });

  // Function to check connection request status
  const checkConnectionStatus = useCallback(async () => {
    if (!user?.uid || !opportunityId) {
      setConnectionStatus({
        hasRequest: false,
        status: null,
        loading: false
      });
      return;
    }

    setConnectionStatus(prev => ({ ...prev, loading: true }));

    try {
      const result = await checkUserConnectionRequest(user.uid, opportunityId);
      
      if (result.success) {
        setConnectionStatus({
          hasRequest: result.data.hasRequest,
          status: result.data.status,
          loading: false
        });
      } else {
        console.warn('Failed to check connection status:', result.error);
        setConnectionStatus({
          hasRequest: false,
          status: null,
          loading: false
        });
      }
    } catch (error) {
      console.error('Error checking connection status:', error);
      setConnectionStatus({
        hasRequest: false,
        status: null,
        loading: false
      });
    }
  }, [user?.uid, opportunityId]);

  // Check connection request status when hook is initialized
  useEffect(() => {
    checkConnectionStatus();
  }, [checkConnectionStatus]);

  // Refresh connection status when refreshTrigger prop changes
  useEffect(() => {
    if (refreshTrigger) {
      checkConnectionStatus();
    }
  }, [refreshTrigger, checkConnectionStatus]);

  // Listen for global refresh events
  useEffect(() => {
    const handleRefreshConnections = (event) => {
      // Refresh all cards, or only the specific opportunity if specified
      if (!event.detail?.opportunityId || event.detail.opportunityId === opportunityId) {
        checkConnectionStatus();
      }
    };

    window.addEventListener('refreshOpportunityConnections', handleRefreshConnections);
    
    return () => {
      window.removeEventListener('refreshOpportunityConnections', handleRefreshConnections);
    };
  }, [opportunityId, checkConnectionStatus]);

  return {
    connectionStatus,
    checkConnectionStatus,
    refreshConnectionStatus: checkConnectionStatus
  };
};

export default useConnectionStatus;